/**
 * @fileoverview Product tabs for the all-products Tracking Demo panel.
 *
 * The dashboard is mounted once per product (Menu & Roles route data carries the
 * product name). Mounted under the "Prism" group it has no single product, so it
 * shows every product at once, with a tab per product to narrow it down. Pure: no
 * Angular, so the rules unit-test directly.
 */

import type {
  ProductConfig, TrackingDemoProductConfig,
} from '../../../core/services/product-config.service';

/** The API's "every product" key: TrackingApiService.scoped() sends no filter. */
export const ALL_PRODUCTS_KEY = 'all';

/** One tab in the product switcher. */
export interface TrackingProductTab {
  /** The backend products.keyname, or {@link ALL_PRODUCTS_KEY}. */
  key: string;
  label: string;
  /** Observer-join config for this product; null on the All tab. */
  config: TrackingDemoProductConfig | null;
}

/** Observer join from the All tab: the call page is shared by every product, and
 *  openObserver() swaps in the session's own product token. */
export const ALL_PRODUCTS_CONFIG: TrackingDemoProductConfig = {
  productKey: ALL_PRODUCTS_KEY,
  observerCallPath: '/instant-demo/call',
  observerHandoffKeyPrefix: 'credeeObserverHandoff',
};

/** True when the route's product group is the cross-product "Prism" panel. */
export function isAllProductsPanel(productName: string | null | undefined): boolean {
  return (productName || '').trim().toLowerCase() === 'prism';
}

/** "All products" first, then every product that runs AI demos, in config order. */
export function trackingProductTabs(configs: readonly ProductConfig[]): TrackingProductTab[] {
  const tabs: TrackingProductTab[] = [
    { key: ALL_PRODUCTS_KEY, label: 'All products', config: null },
  ];
  for (const c of configs) {
    if (c.trackingDemo) {
      tabs.push({ key: c.trackingDemo.productKey, label: c.displayName, config: c.trackingDemo });
    }
  }
  return tabs;
}
